<template>
  <v-container fluid class="boletim-page pa-6">
    <!-- Cabecalho -->
    <v-row align="center" class="mb-2" no-gutters>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-icon color="primary" large class="mr-3">mdi-ballot-outline</v-icon>
          <div>
            <h2 class="titulo-pagina">Apuração — Boletim de Urna</h2>
            <div class="caption grey--text">
              Votos por seção × cadastros: fator distribuído em cada cadastro da equipe
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-md-end align-center mt-3 mt-md-0">
        <v-select
          v-model="select_equipe"
          :items="candidatos"
          item-text="rotulo"
          item-value="id"
          label="Equipe / candidato"
          dense
          hide-details
          outlined
          class="mr-3"
          style="max-width: 320px"
          @change="carregarResumo"
        ></v-select>
        <v-btn color="primary" outlined :loading="loading" :disabled="!select_equipe" @click="carregarResumo">
          <v-icon left>mdi-refresh</v-icon>Atualizar
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- Importacao -->
      <v-col cols="12" md="5">
        <v-card outlined class="pa-4 fill-height">
          <div class="subtitle-1 font-weight-medium mb-1">Importar boletim</div>
          <div class="caption grey--text mb-3">
            Arquivo "votação por seção" do TSE (.zip ou .csv) de um único candidato.
            O número do candidato no arquivo precisa ser igual à chapa da equipe
            <strong v-if="candidatoAtual">({{ candidatoAtual.chapa || "sem chapa" }})</strong>.
            Reimportar substitui a apuração anterior.
          </div>
          <v-file-input
            v-model="arquivo"
            accept=".zip,.csv"
            label="Selecione o arquivo"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            outlined
            dense
            show-size
            :disabled="importando || !select_equipe"
          ></v-file-input>
          <v-progress-linear v-if="importando" indeterminate color="primary" class="mb-2"></v-progress-linear>
          <div v-if="importando" class="caption grey--text mb-2">
            Importando e distribuindo o fator nos cadastros — pode levar até um minuto.
          </div>
          <div class="d-flex flex-wrap">
            <v-btn
              color="primary"
              depressed
              class="mr-2 mb-2"
              :loading="importando"
              :disabled="!arquivo || !select_equipe || recalculando"
              @click="importar"
            >
              <v-icon left>mdi-upload</v-icon>Importar e calcular
            </v-btn>
            <v-btn
              outlined
              class="mb-2"
              :loading="recalculando"
              :disabled="!importacao || importando"
              @click="recalcular"
            >
              <v-icon left>mdi-calculator-variant-outline</v-icon>Recalcular fator
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <!-- Situacao da importacao -->
      <v-col cols="12" md="7">
        <v-card outlined class="pa-4 fill-height">
          <div class="subtitle-1 font-weight-medium mb-2">Apuração carregada</div>
          <v-skeleton-loader v-if="loading" type="list-item-three-line"></v-skeleton-loader>
          <div v-else-if="!importacao" class="grey--text">
            Nenhum boletim importado para esta equipe.
          </div>
          <div v-else>
            <div class="text-h6">{{ importacao.nm_votavel }}</div>
            <div class="grey--text mb-2">
              {{ importacao.ds_cargo }} · nº {{ importacao.nr_votavel }} · {{ importacao.aa_eleicao }}
            </div>
            <div class="caption">
              <v-icon x-small class="mr-1">mdi-file-outline</v-icon>{{ importacao.arquivo }}
            </div>
            <div class="caption">
              <v-icon x-small class="mr-1">mdi-upload</v-icon>Importado em {{ dataHora(importacao.created_at) }}
            </div>
            <div class="caption">
              <v-icon x-small class="mr-1">mdi-calculator-variant-outline</v-icon>
              Fator calculado em {{ dataHora(importacao.fator_calculado_em) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <template v-if="importacao">
      <!-- KPIs -->
      <v-row>
        <v-col v-for="kpi in kpis" :key="kpi.titulo" cols="6" sm="4" md="2">
          <v-card outlined class="pa-3 fill-height">
            <v-skeleton-loader v-if="loading" type="list-item-two-line"></v-skeleton-loader>
            <template v-else>
              <div class="caption grey--text text-uppercase">
                {{ kpi.titulo }}<dica-metrica :texto="kpi.dica" />
              </div>
              <div class="kpi-valor" :class="kpi.cor">{{ kpi.valor }}</div>
              <div class="caption grey--text">{{ kpi.legenda }}</div>
            </template>
          </v-card>
        </v-col>
      </v-row>

      <!-- Por zona -->
      <v-card outlined class="mt-4">
        <v-card-title class="subtitle-1 font-weight-medium">
          Fator por zona eleitoral
          <v-spacer></v-spacer>
          <v-text-field
            v-model="buscaZona"
            prepend-inner-icon="mdi-magnify"
            label="Zona ou município"
            dense
            hide-details
            outlined
            clearable
            style="max-width: 260px"
          ></v-text-field>
        </v-card-title>
        <v-card-subtitle class="caption">
          Fator da seção = votos ÷ cadastros, com teto 1. Cada cadastro recebe o fator
          da sua seção; a pontuação é a soma desses fatores. Clique na zona para ver
          as seções.
        </v-card-subtitle>
        <v-data-table
          :headers="headersZona"
          :items="zonas"
          :search="buscaZona"
          :loading="loading"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          item-key="zona"
          sort-by="zona"
          dense
          class="tabela-zona"
          @click:row="abrirZona"
        >
          <template v-for="h in comDica(headersZona)" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
          <template v-slot:[`item.cadastros`]="{ item }">{{ numero(item.cadastros) }}</template>
          <template v-slot:[`item.votos`]="{ item }">{{ numero(item.votos) }}</template>
          <template v-slot:[`item.conversao`]="{ item }">{{ percentual(item.conversao) }}</template>
          <template v-slot:[`item.pontuacao`]="{ item }">{{ decimal(item.pontuacao) }}</template>
          <template v-slot:[`item.fator`]="{ item }">
            <v-chip small :color="corFator(item.fator)" dark>{{ fator(item.fator) }}</v-chip>
          </template>
          <template v-slot:[`item.votos_sem_cadastro`]="{ item }">
            <span :class="item.votos_sem_cadastro > 0 ? '' : 'grey--text'">{{ numero(item.votos_sem_cadastro) }}</span>
          </template>
        </v-data-table>
      </v-card>

      <!-- Por meta -->
      <v-card outlined class="mt-4">
        <v-card-title class="subtitle-1 font-weight-medium">Pontuação por meta</v-card-title>
        <v-card-subtitle class="caption">
          Fator meta = votos da seção ÷ cadastros só desta meta na seção (teto 1). Cada meta
          é comparada isoladamente com o boletim, então as metas somadas podem passar do
          total de votos.
        </v-card-subtitle>
        <v-data-table
          :headers="headersMeta"
          :items="metas"
          :loading="loading"
          :items-per-page="-1"
          hide-default-footer
          item-key="idmeta"
          sort-by="pontuacao_meta"
          sort-desc
          dense
        >
          <template v-for="h in comDica(headersMeta)" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
          </template>
          <template v-slot:[`item.alvo`]="{ item }">{{ numero(item.alvo) }}</template>
          <template v-slot:[`item.cadastros`]="{ item }">{{ numero(item.cadastros) }}</template>
          <template v-slot:[`item.pontuacao`]="{ item }">{{ decimal(item.pontuacao) }}</template>
          <template v-slot:[`item.pontuacao_meta`]="{ item }">
            <strong>{{ decimal(item.pontuacao_meta) }}</strong>
          </template>
          <template v-slot:[`item.fator_meta`]="{ item }">
            <v-chip small :color="corFator(item.fator_meta)" dark>{{ fator(item.fator_meta) }}</v-chip>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Secoes de uma zona -->
    <v-dialog v-model="dialogZona" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          Zona {{ zonaAberta && zonaAberta.zona }}
          <span class="grey--text ml-2 body-2">{{ zonaAberta && zonaAberta.municipios }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogZona = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense class="mb-2" align="center">
            <v-col cols="12" sm="7">
              <v-btn-toggle v-model="filtroSecao" mandatory dense color="primary">
                <v-btn small value="todas">Todas</v-btn>
                <v-btn small value="com_voto">Com voto</v-btn>
                <v-btn small value="sem_voto">Cadastro sem voto</v-btn>
                <v-btn small value="sem_cadastro">Voto sem cadastro</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="buscaSecao"
                prepend-inner-icon="mdi-magnify"
                label="Seção, local ou bairro"
                dense
                hide-details
                outlined
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table
            :headers="headersSecao"
            :items="secoesFiltradas"
            :search="buscaSecao"
            :loading="loadingSecoes"
            :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
            item-key="secao"
            sort-by="secao"
            dense
          >
            <template v-for="h in comDica(headersSecao)" v-slot:[`header.${h.value}`]="{ header }">
              <span :key="h.value">{{ header.text }}<dica-metrica :texto="header.dica" /></span>
            </template>
            <template v-slot:[`item.secao`]="{ item }">
              {{ item.secao }}
              <v-tooltip v-if="!item.secao_existe" bottom>
                <template v-slot:activator="{ on }">
                  <v-icon x-small color="grey" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                Seção não encontrada no cadastro de seções do TSE
              </v-tooltip>
            </template>
            <template v-slot:[`item.fator`]="{ item }">
              <v-chip x-small :color="corFator(item.fator)" dark>{{ fator(item.fator) }}</v-chip>
            </template>
            <template v-slot:[`item.pontuacao`]="{ item }">{{ decimal(item.fator * item.cadastros) }}</template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ApuracaoAPI from "./../../api/apuracao";
import DicaMetrica from "./../analise/DicaMetrica.vue";
import { DESCRICOES } from "./../analise/descricoesMetricas";

const nf0 = new Intl.NumberFormat("pt-BR");
const nf2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf4 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const pct = new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 1 });

export default {
  name: "BoletimUrna",
  components: { DicaMetrica },
  data: () => ({
    candidatos: [],
    select_equipe: null,
    arquivo: null,
    importando: false,
    recalculando: false,
    loading: false,
    resumo: null,
    zonas: [],
    metas: [],
    buscaZona: "",
    dialogZona: false,
    zonaAberta: null,
    secoes: [],
    loadingSecoes: false,
    filtroSecao: "todas",
    buscaSecao: "",
    headersZona: [
      { text: "Zona", value: "zona", width: 80 },
      { text: "Município(s)", value: "municipios" },
      { text: "Cadastros", value: "cadastros", align: "end", dica: DESCRICOES.cadastros },
      { text: "Votos", value: "votos", align: "end", dica: DESCRICOES.votos },
      { text: "Conversão", value: "conversao", align: "end", dica: DESCRICOES.conversao },
      { text: "Pontuação", value: "pontuacao", align: "end", dica: DESCRICOES.pontuacao },
      { text: "Fator", value: "fator", align: "center", dica: DESCRICOES.fatorZona },
      { text: "Seções c/ voto", value: "secoes_com_voto", align: "end", dica: DESCRICOES.secoesComVoto },
      { text: "Votos sem cadastro", value: "votos_sem_cadastro", align: "end", dica: DESCRICOES.votosSemCadastro },
    ],
    headersMeta: [
      { text: "Meta", value: "descricao" },
      { text: "Alvo", value: "alvo", align: "end", dica: DESCRICOES.alvo },
      { text: "Assessores", value: "assessores", align: "end", dica: DESCRICOES.assessoresMeta },
      { text: "Cadastros", value: "cadastros", align: "end", dica: DESCRICOES.cadastros },
      { text: "Pontuação geral", value: "pontuacao", align: "end", dica: DESCRICOES.pontuacao },
      { text: "Pontuação meta", value: "pontuacao_meta", align: "end", dica: DESCRICOES.pontuacaoMeta },
      { text: "Fator meta", value: "fator_meta", align: "center", dica: DESCRICOES.fatorMeta },
    ],
    headersSecao: [
      { text: "Seção", value: "secao", width: 90 },
      { text: "Município", value: "municipio" },
      { text: "Local de votação", value: "local_votacao" },
      { text: "Bairro", value: "bairro" },
      { text: "Cadastros", value: "cadastros", align: "end", dica: DESCRICOES.cadastros },
      { text: "Votos", value: "votos", align: "end", dica: DESCRICOES.votos },
      { text: "Fator", value: "fator", align: "center", dica: DESCRICOES.fator },
      { text: "Pontuação", value: "pontuacao", align: "end", dica: DESCRICOES.pontuacao },
    ],
  }),
  computed: {
    candidatoAtual() {
      return this.candidatos.find((c) => c.id === this.select_equipe) || null;
    },
    importacao() {
      return this.resumo ? this.resumo.importacao : null;
    },
    kpis() {
      const t = (this.resumo && this.resumo.totais) || {};
      const votos = Number(t.votos) || 0;
      const emCadastro = Number(t.votos_em_secao_com_cadastro) || 0;
      return [
        { titulo: "Votos", dica: DESCRICOES.votos, valor: this.numero(votos), legenda: `${this.numero(t.secoes_com_voto)} seções com voto`, cor: "primary--text" },
        { titulo: "Cadastros", dica: DESCRICOES.cadastros, valor: this.numero(t.cadastros), legenda: `${this.numero(t.secoes_com_cadastro)} seções com cadastro` },
        { titulo: "Votos onde há cadastro", dica: "Votos em seções onde a equipe tem pelo menos um cadastro.", valor: this.numero(emCadastro), legenda: votos ? `${this.percentual(emCadastro / votos)} dos votos` : "" },
        { titulo: "Pontuação", dica: DESCRICOES.pontuacao, valor: this.decimal(t.pontuacao), legenda: "soma dos fatores", cor: "green--text text--darken-2" },
        { titulo: "Fator médio", dica: DESCRICOES.aproveitamento, valor: this.fator(t.fator_medio), legenda: "pontuação ÷ cadastros" },
        {
          titulo: "Votos sem cadastro",
          dica: DESCRICOES.votosSemCadastro,
          valor: this.numero(t.votos_sem_cadastro),
          legenda: Number(t.cadastros_secao_inexistente)
            ? `${this.numero(t.cadastros_secao_inexistente)} cadastros em seção inexistente`
            : "seções sem nenhum cadastro",
        },
      ];
    },
    secoesFiltradas() {
      switch (this.filtroSecao) {
        case "com_voto":
          return this.secoes.filter((s) => s.votos > 0);
        case "sem_voto":
          return this.secoes.filter((s) => s.cadastros > 0 && s.votos === 0);
        case "sem_cadastro":
          return this.secoes.filter((s) => s.cadastros === 0);
        default:
          return this.secoes;
      }
    },
  },
  mounted() {
    this.buscarCandidatos();
  },
  methods: {
    comDica(headers) {
      return headers.filter((h) => h.dica);
    },
    numero(v) {
      return nf0.format(Number(v) || 0);
    },
    decimal(v) {
      return nf2.format(Number(v) || 0);
    },
    fator(v) {
      return nf4.format(Number(v) || 0);
    },
    percentual(v) {
      return pct.format(Number(v) || 0);
    },
    dataHora(v) {
      if (!v) return "—";
      const d = new Date(String(v).replace(" ", "T"));
      return isNaN(d) ? v : d.toLocaleString("pt-BR");
    },
    corFator(v) {
      const f = Number(v) || 0;
      if (f >= 0.5) return "green darken-1";
      if (f >= 0.25) return "light-green darken-1";
      if (f >= 0.1) return "amber darken-2";
      if (f > 0) return "orange darken-3";
      return "grey";
    },

    tratarErro(error, padrao) {
      if (error.response && error.response.status === 401) {
        this.Helpers.logout();
        return;
      }
      const msg = (error.response && error.response.data && error.response.data.message) || padrao;
      this.Helpers.exibirMensagem(msg, "error", 8000);
    },

    salvarToken(response) {
      if (response.data && response.data.token) localStorage.setItem("token", response.data.token);
    },

    buscarCandidatos() {
      const headers = this.Helpers.getHeaderRequisicao();
      return ApuracaoAPI.getCandidatos(headers)
        .then((response) => {
          this.salvarToken(response);
          this.candidatos = (response.data.candidatos || []).map((c) => ({
            ...c,
            rotulo: c.nm_votavel ? `${c.name} — ${c.nm_votavel} (${c.chapa})` : `${c.name} (chapa ${c.chapa || "—"})`,
          }));
          if (!this.select_equipe && this.candidatos.length) {
            this.select_equipe = this.candidatos[0].id;
            this.carregarResumo();
          }
        })
        .catch((e) => this.tratarErro(e, "Erro ao carregar os candidatos."));
    },

    aplicarResumo(data) {
      this.resumo = data.resumo;
      if (data.metas) {
        this.metas = data.metas.map((m) => {
          const cad = Number(m.cadastros) || 0;
          return {
            ...m,
            alvo: Number(m.alvo) || 0,
            assessores: Number(m.assessores) || 0,
            cadastros: cad,
            pontuacao: Number(m.pontuacao) || 0,
            pontuacao_meta: Number(m.pontuacao_meta) || 0,
            fator_meta: cad ? Number(m.pontuacao_meta) / cad : 0,
          };
        });
      }
      if (data.zonas) {
        this.zonas = data.zonas.map((z) => {
          const cad = Number(z.cadastros) || 0;
          return {
            ...z,
            cadastros: cad,
            votos: Number(z.votos) || 0,
            pontuacao: Number(z.pontuacao) || 0,
            secoes_com_voto: Number(z.secoes_com_voto) || 0,
            votos_sem_cadastro: Number(z.votos_sem_cadastro) || 0,
            conversao: cad ? Number(z.votos) / cad : 0,
            fator: cad ? Number(z.pontuacao) / cad : 0,
          };
        });
      }
    },

    carregarResumo() {
      if (!this.select_equipe) return;
      this.loading = true;
      const headers = this.Helpers.getHeaderRequisicao();
      ApuracaoAPI.getResumo(headers, this.select_equipe)
        .then((response) => {
          this.salvarToken(response);
          this.aplicarResumo(response.data);
        })
        .catch((e) => this.tratarErro(e, "Erro ao carregar a apuração."))
        .finally(() => {
          this.loading = false;
        });
    },

    importar() {
      if (!this.arquivo || !this.select_equipe) return;
      this.importando = true;
      const headers = this.Helpers.getHeaderRequisicao();
      ApuracaoAPI.importar(headers, this.select_equipe, this.arquivo)
        .then((response) => {
          this.salvarToken(response);
          const imp = response.data.importacao;
          this.Helpers.exibirMensagem(
            `Boletim de ${imp.nm_votavel} importado: ${this.numero(imp.qt_votos)} votos em ${this.numero(imp.qt_secoes)} seções.`,
            "success",
            6000
          );
          this.arquivo = null;
          this.buscarCandidatos();
          this.carregarResumo();
        })
        .catch((e) => this.tratarErro(e, "Erro ao importar o boletim."))
        .finally(() => {
          this.importando = false;
        });
    },

    recalcular() {
      this.recalculando = true;
      const headers = this.Helpers.getHeaderRequisicao();
      ApuracaoAPI.recalcular(headers, this.select_equipe)
        .then((response) => {
          this.salvarToken(response);
          this.Helpers.exibirMensagem("Fator redistribuído com o cadastro atual.", "success", 5000);
          this.carregarResumo();
        })
        .catch((e) => this.tratarErro(e, "Erro ao recalcular o fator."))
        .finally(() => {
          this.recalculando = false;
        });
    },

    abrirZona(zona) {
      this.zonaAberta = zona;
      this.filtroSecao = "todas";
      this.buscaSecao = "";
      this.secoes = [];
      this.dialogZona = true;
      this.loadingSecoes = true;
      const headers = this.Helpers.getHeaderRequisicao();
      ApuracaoAPI.getSecoes(headers, this.select_equipe, zona.zona)
        .then((response) => {
          this.salvarToken(response);
          this.secoes = (response.data.secoes || []).map((s) => ({
            ...s,
            cadastros: Number(s.cadastros) || 0,
            votos: Number(s.votos) || 0,
            fator: Number(s.fator) || 0,
            secao_existe: Number(s.secao_existe) === 1,
          }));
        })
        .catch((e) => this.tratarErro(e, "Erro ao carregar as seções."))
        .finally(() => {
          this.loadingSecoes = false;
        });
    },
  },
};
</script>

<style scoped>
.titulo-pagina {
  color: #0e314a;
  font-weight: 600;
}
.kpi-valor {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.8rem;
}
.tabela-zona >>> tbody tr {
  cursor: pointer;
}
</style>
